// React
import React, {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react'
// Material
import { Box, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
// Logic
import { Hall } from '@weblite/ts-action/hall'
import { hallConstraints } from '$logic/hall/constant'
// Common
import { HallAvatar } from '$common/avatar'
import { PersonIcon } from '$common/icons/person'
import { Label } from '$common/label'
import { TextInput, MultilineInput } from '$common/input'
import { WithEditableAvatar } from '$common/with-editable-avatar'
// Helpers
import { nothing } from '$func/utils'
// Localization
import { t } from '$i18n'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { ProfilePictureMenu } from '$comp/modals/menus/profile-picture-menu'

const StyledHallAvatar = styled(HallAvatar)`
  border: 5px solid white;
  // TODO: Pick random bgcolor
  background-color: #455a64;
`

interface Props {
  hall: Hall
  editable: boolean
  isGroup: boolean
}

export interface HallSettingsProfileApiType {
  getName: () => string
  getDescription: () => string
  getImage: () => string
}

export const HallSettingsProfile = forwardRef(function HallSettingsProfile(
  { hall, editable, isGroup }: Props,
  ref: ForwardedRef<HallSettingsProfileApiType>,
) {
  const [name, setName] = useState<string>(hall.name)
  const [desc, setDesc] = useState<string>(hall.description)
  const [image, setImage] = useState<string | undefined>(hall.image)

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuLocation, setMenuLocation] = useState({ xAxis: 0, yAxis: 0 })
  const onMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuLocation({ xAxis: e.clientX, yAxis: e.clientY })
    setIsMenuOpen(!isMenuOpen)
  }

  useImperativeHandle(
    ref,
    () =>
      ({
        getName: () => name,
        getDescription: () => desc,
        getImage: () => image,
      }) as HallSettingsProfileApiType,
    [name, desc, image],
  )

  return (
    <Stack sx={{ padding: '16px' }}>
      {editable ? (
        <WithEditableAvatar onSelectClick={onMenuClick}>
          <StyledHallAvatar
            roomId={hall.roomId}
            size={AvatarSize._3XL}
            onClick={nothing}
            overrideImage={image}
          >
            <PersonIcon sx={{ width: '50px', height: '50px' }} />
          </StyledHallAvatar>
          {isMenuOpen && (
            <ProfilePictureMenu
              showDelete={!!hall.image}
              onSubmit={setImage}
              onClose={() => setIsMenuOpen(false)}
              xAxis={menuLocation.xAxis}
              yAxis={menuLocation.yAxis}
            />
          )}
        </WithEditableAvatar>
      ) : (
        <StyledHallAvatar
          roomId={hall.roomId}
          size={AvatarSize._3XL}
          onClick={nothing}
          overrideImage={image}
          sx={{ margin: 'auto' }}
        >
          <PersonIcon sx={{ width: '50px', height: '50px' }} />
        </StyledHallAvatar>
      )}
      <Box sx={{ paddingTop: '20px' }}>
        <Label
          title={
            isGroup
              ? t('profile.hall.groupName')
              : t('profile.hall.channelName')
          }
        />
        <TextInput
          placeholder={
            isGroup
              ? t('profile.hall.enterGroupName')
              : t('profile.hall.enterChannelName')
          }
          value={name}
          onChange={setName}
          maxLength={hallConstraints.name.maxLength}
          disabled={!editable}
        />

        <Label title={t('profile.hall.description')} />
        <MultilineInput
          value={desc}
          placeholder={
            editable
              ? isGroup
                ? t('profile.hall.enterGroupDescription')
                : t('profile.hall.enterChannelDescription')
              : t('profile.hall.noDescription')
          }
          maxLength={hallConstraints.description.maxLength}
          onChange={setDesc}
          disabled={!editable}
        />
      </Box>
    </Stack>
  )
})
